import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useRef, useState } from 'react';
import L from 'leaflet';
import 'leaflet.markercluster';
import 'leaflet.markercluster/dist/MarkerCluster.css';
import 'leaflet.markercluster/dist/MarkerCluster.Default.css';
import 'leaflet.heat';
import { Flame, MapPin, Navigation, Home, X, Route as RouteIcon, Loader2, Clock, Shuffle, Layers } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@ci-ville/ui';
import { api } from '@/lib/api-client';
import { createBaseLayer, QUEBEC_CENTER } from '@/lib/map';
import { groupMarkerIcon, reportMarkerIcon } from '@/lib/map-markers';
import { STATUS_COLORS } from '@/lib/report-colors';
import { AddressSearch } from './AddressSearch';
/** Couleurs des classes Getis-Ord (points chauds / froids) */
export const HOTSPOT_STYLES = {
    hot99: { color: '#B91C1C', label: 'Point chaud (99 %)', opacity: 0.6 },
    hot95: { color: '#EA580C', label: 'Point chaud (95 %)', opacity: 0.5 },
    hot90: { color: '#F59E0B', label: 'Point chaud (90 %)', opacity: 0.4 },
    cold95: { color: '#2563EB', label: 'Point froid (95 %)', opacity: 0.3 },
    none: { color: '#64748B', label: 'Non significatif', opacity: 0.08 },
};
const PRIORITY_SIZES = { URGENT: 44, HIGH: 36, NORMAL: 30, LOW: 24 };
const PRIORITY_WEIGHT = { URGENT: 1.0, HIGH: 0.7, NORMAL: 0.4, LOW: 0.2 };
function createMarkerIcon(r, highlighted) {
    return reportMarkerIcon({
        color: STATUS_COLORS[r.status],
        iconName: r.category?.iconName,
        size: PRIORITY_SIZES[r.priorityLevel],
        highlighted,
        pulse: r.priorityLevel === 'URGENT',
    });
}
export function ReportMap({ reports, onSelect, selectedId, tenantId, center, onBoundsChange, zones, hotspots, layers, onLayersChange, focus, zoomTo, }) {
    const boundsCbRef = useRef(onBoundsChange);
    boundsCbRef.current = onBoundsChange;
    const zonesLayerRef = useRef(null);
    const hotLayerRef = useRef(null);
    const [layersMenu, setLayersMenu] = useState(false);
    const containerRef = useRef(null);
    const mapRef = useRef(null);
    const clusterRef = useRef(null);
    const heatLayerRef = useRef(null);
    const routeLineRef = useRef(null);
    const routeNumberedMarkersRef = useRef([]);
    const searchMarkerRef = useRef(null);
    const initialBoundsRef = useRef(null);
    const [heatOn, setHeatOn] = useState(false);
    const [routeMode, setRouteMode] = useState(false);
    const [routeStops, setRouteStops] = useState([]);
    const [optimize, setOptimize] = useState(true);
    const [routeTracing, setRouteTracing] = useState(false);
    const [routeSummary, setRouteSummary] = useState(null);
    useEffect(() => {
        if (!containerRef.current || mapRef.current)
            return;
        const start = center ? [center.latitude, center.longitude] : QUEBEC_CENTER;
        const map = L.map(containerRef.current, { zoomControl: false, attributionControl: true }).setView(start, center ? 13 : 8);
        L.control.zoom({ position: 'topright' }).addTo(map);
        createBaseLayer().addTo(map);
        const cluster = L.markerClusterGroup({
            maxClusterRadius: 60, disableClusteringAtZoom: 16, showCoverageOnHover: false,
            iconCreateFunction: (c) => groupMarkerIcon(c.getChildCount()),
        });
        map.addLayer(cluster);
        mapRef.current = map;
        clusterRef.current = cluster;
        const emitBounds = () => {
            const b = map.getBounds();
            boundsCbRef.current?.({ west: b.getWest(), south: b.getSouth(), east: b.getEast(), north: b.getNorth() });
        };
        map.on('moveend', emitBounds);
        emitBounds();
        // Recalcule la taille quand les panneaux latéraux s'ouvrent ou se ferment
        const resizeObserver = new ResizeObserver(() => map.invalidateSize());
        resizeObserver.observe(containerRef.current);
        return () => {
            resizeObserver.disconnect();
            map.remove();
            mapRef.current = null;
            clusterRef.current = null;
            heatLayerRef.current = null;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    useEffect(() => {
        const cluster = clusterRef.current;
        const map = mapRef.current;
        if (!cluster || !map)
            return;
        cluster.clearLayers();
        const points = [];
        const markers = [];
        reports.forEach((r) => {
            if (r.latitude == null || r.longitude == null)
                return;
            const highlighted = r.id === selectedId || routeStops.some((s) => s.id === r.id);
            const marker = L.marker([r.latitude, r.longitude], { icon: createMarkerIcon(r, highlighted), zIndexOffset: highlighted ? 500 : 0 })
                .bindTooltip(r.title, { direction: 'top', offset: [0, -8] });
            marker.on('click', () => {
                if (routeMode) {
                    setRouteStops((prev) => (prev.some((s) => s.id === r.id) ? prev.filter((s) => s.id !== r.id) : [...prev, r]));
                }
                else {
                    onSelect?.(r.id);
                }
            });
            markers.push(marker);
            points.push(L.latLng(r.latitude, r.longitude));
        });
        cluster.addLayers(markers);
        if (points.length > 0 && !initialBoundsRef.current) {
            const bounds = L.latLngBounds(points);
            initialBoundsRef.current = bounds;
            map.fitBounds(bounds, { padding: [40, 40], maxZoom: 14 });
        }
    }, [reports, onSelect, routeMode, routeStops, selectedId]);
    useEffect(() => {
        const map = mapRef.current;
        if (!map)
            return;
        if (heatLayerRef.current) {
            map.removeLayer(heatLayerRef.current);
            heatLayerRef.current = null;
        }
        if (heatOn) {
            const pts = reports
                .filter((r) => r.latitude != null && r.longitude != null)
                .map((r) => [r.latitude, r.longitude, PRIORITY_WEIGHT[r.priorityLevel]]);
            if (pts.length > 0) {
                heatLayerRef.current = L
                    .heatLayer(pts, { radius: 30, blur: 22, maxZoom: 16, gradient: { 0.2: '#3B82F6', 0.5: '#EAB308', 0.7: '#EA580C', 1.0: '#DC2626' } })
                    .addTo(map);
            }
        }
    }, [heatOn, reports]);
    // Couche des quartiers : contours, nom et compteurs au survol
    useEffect(() => {
        const map = mapRef.current;
        if (!map)
            return;
        zonesLayerRef.current?.remove();
        zonesLayerRef.current = null;
        if (!layers?.zones || !zones?.length)
            return;
        zonesLayerRef.current = L.geoJSON({ type: 'FeatureCollection', features: zones.filter((z) => z.geometry).map((z) => ({ type: 'Feature', properties: z, geometry: z.geometry })) }, {
            style: { color: '#1D4ED8', weight: 2, dashArray: '6 4', fillOpacity: 0.03 },
            onEachFeature: (f, layer) => {
                const z = f.properties;
                layer.bindTooltip(`<strong>${z.name}</strong><br>${z.reports} signalement(s), ${z.open} ouvert(s)`, { sticky: true });
            },
        }).addTo(map);
        zonesLayerRef.current.bringToBack();
    }, [layers?.zones, zones]);
    // Couche des points chauds : hexagones colorés selon le score Getis-Ord
    useEffect(() => {
        const map = mapRef.current;
        if (!map)
            return;
        hotLayerRef.current?.remove();
        hotLayerRef.current = null;
        if (!layers?.hotspots || !hotspots?.cells.length)
            return;
        hotLayerRef.current = L.geoJSON({ type: 'FeatureCollection', features: hotspots.cells.map((c) => ({ type: 'Feature', properties: c, geometry: c.geometry })) }, {
            style: (f) => {
                const s = HOTSPOT_STYLES[(f?.properties).cls] ?? HOTSPOT_STYLES.none;
                return { color: s.color, weight: 0.8, fillColor: s.color, fillOpacity: s.opacity };
            },
            onEachFeature: (f, layer) => {
                const c = f.properties;
                layer.bindTooltip(`${c.n} signalement(s) · ${HOTSPOT_STYLES[c.cls]?.label} (z = ${c.z})`, { sticky: true });
            },
        }).addTo(map);
    }, [layers?.hotspots, hotspots]);
    // Clic dans la liste : zoom sur le signalement, une fois la carte redimensionnée
    // (la fiche qui s'ouvre à droite la rétrécit). Au zoom 17, il n'est plus regroupé.
    useEffect(() => {
        const target = zoomTo && reports.find((r) => r.id === zoomTo.id);
        if (!target || target.latitude == null || target.longitude == null)
            return;
        const latlng = L.latLng(target.latitude, target.longitude);
        const timer = window.setTimeout(() => {
            const map = mapRef.current;
            if (!map)
                return;
            map.invalidateSize({ pan: false });
            map.flyTo(latlng, Math.max(map.getZoom(), 17), { duration: 0.6 });
        }, 80);
        return () => window.clearTimeout(timer);
    }, [zoomTo?.id, zoomTo?.n]); // eslint-disable-line react-hooks/exhaustive-deps
    // Lien depuis la page Statistiques : zoom sur un lieu précis
    useEffect(() => {
        if (focus && mapRef.current)
            mapRef.current.setView([focus.latitude, focus.longitude], 18);
    }, [focus?.latitude, focus?.longitude]); // eslint-disable-line react-hooks/exhaustive-deps
    /** Vue initiale : l'emprise des signalements, sinon le centre de la ville. */
    const homeView = () => {
        const map = mapRef.current;
        if (!map)
            return;
        if (initialBoundsRef.current)
            map.fitBounds(initialBoundsRef.current, { padding: [40, 40], maxZoom: 14 });
        else if (center)
            map.setView([center.latitude, center.longitude], 13);
        else
            map.setView(QUEBEC_CENTER, 8);
    };
    const showAddress = (lat, lng, label) => {
        const map = mapRef.current;
        if (!map)
            return;
        searchMarkerRef.current?.remove();
        searchMarkerRef.current = L.marker([lat, lng], {
            icon: L.divIcon({
                className: 'ci-search-marker',
                html: `<div style="width:22px;height:22px;border-radius:50% 50% 50% 0;transform:rotate(-45deg);background:#0F172A;border:3px solid white;box-shadow:0 2px 8px rgba(0,0,0,0.4)"></div>`,
                iconSize: [22, 22],
                iconAnchor: [11, 22],
            }),
            zIndexOffset: 2000,
        })
            .bindPopup(label)
            .addTo(map)
            .openPopup();
        map.setView([lat, lng], 17);
    };
    const clearDrawnRoute = () => {
        const map = mapRef.current;
        if (map && routeLineRef.current) {
            map.removeLayer(routeLineRef.current);
            routeLineRef.current = null;
        }
        routeNumberedMarkersRef.current.forEach((m) => map?.removeLayer(m));
        routeNumberedMarkersRef.current = [];
        setRouteSummary(null);
    };
    const traceRoute = async () => {
        const map = mapRef.current;
        if (!map || routeStops.length < 2) {
            toast.error('Sélectionnez au moins 2 signalements');
            return;
        }
        clearDrawnRoute();
        setRouteTracing(true);
        try {
            const stops = routeStops.filter((s) => s.latitude != null && s.longitude != null);
            const route = await api.geo.route(stops, optimize);
            const ordered = route.order.map((i) => stops[i]);
            setRouteStops(ordered);
            routeLineRef.current = L.polyline(route.coordinates, { color: '#298FC2', weight: 5, opacity: 0.85 }).addTo(map);
            ordered.forEach((s, i) => {
                const isLast = i === ordered.length - 1;
                const bg = i === 0 ? '#16A34A' : isLast ? '#DC2626' : '#298FC2';
                const numIcon = L.divIcon({
                    className: 'ci-route-num',
                    html: `<div style="width:34px;height:34px;background:${bg};color:white;border:3px solid white;border-radius:50%;display:flex;align-items:center;justify-content:center;font-weight:700;font-size:14px;box-shadow:0 2px 8px rgba(0,0,0,0.4)">${i + 1}</div>`,
                    iconSize: [34, 34],
                    iconAnchor: [17, 17],
                });
                routeNumberedMarkersRef.current.push(L.marker([s.latitude, s.longitude], { icon: numIcon, zIndexOffset: 1000 }).addTo(map));
            });
            map.fitBounds(L.polyline(route.coordinates).getBounds(), { padding: [60, 60] });
            setRouteSummary({ distanceKm: Math.round(route.distanceMeters / 100) / 10, durationMin: Math.round(route.durationSeconds / 60) });
            toast.success(optimize ? 'Itinéraire optimisé tracé' : 'Itinéraire tracé');
        }
        catch (err) {
            toast.error(err instanceof Error ? err.message : 'Erreur lors du tracé');
        }
        finally {
            setRouteTracing(false);
        }
    };
    const openGoogleMapsRoute = () => {
        if (routeStops.length === 0)
            return;
        const stops = routeStops.map((s) => `${s.latitude},${s.longitude}`).join('/');
        window.open(`https://www.google.com/maps/dir/${stops}/`, '_blank', 'noopener');
    };
    const cancelRoute = () => {
        setRouteMode(false);
        setRouteStops([]);
        clearDrawnRoute();
    };
    return (_jsxs("div", { className: "relative h-full w-full", children: [_jsx("div", { ref: containerRef, className: "absolute inset-0", role: "application", "aria-label": "Carte des signalements" }), _jsx("div", { className: "absolute left-1/2 top-3 z-[1000] w-[min(26rem,calc(100%-9rem))] -translate-x-1/2", children: _jsx(AddressSearch, { tenantId: tenantId, onSelect: (r) => showAddress(r.latitude, r.longitude, r.label), inputClassName: "bg-card shadow-md" }) }), _jsxs("div", { className: "absolute left-3 top-3 z-[1000] flex flex-col gap-1 rounded-lg border border-border bg-card p-1 shadow-md", children: [_jsx("button", { onClick: homeView, title: "Revenir \u00E0 la vue initiale", className: "flex size-8 items-center justify-center rounded text-muted-foreground hover:bg-muted hover:text-foreground", children: _jsx(Home, { className: "size-4" }) }), _jsx("button", { onClick: () => setHeatOn((v) => !v), title: heatOn ? 'Masquer la vue chaleur' : 'Afficher la vue chaleur', className: `flex size-8 items-center justify-center rounded ${heatOn ? 'bg-brand text-white' : 'text-muted-foreground hover:bg-muted hover:text-foreground'}`, children: _jsx(Flame, { className: "size-4" }) }), _jsx("button", { onClick: () => (routeMode ? cancelRoute() : setRouteMode(true)), title: routeMode ? 'Annuler la tournée' : 'Créer une tournée', className: `flex size-8 items-center justify-center rounded ${routeMode ? 'bg-brand text-white' : 'text-muted-foreground hover:bg-muted hover:text-foreground'}`, children: _jsx(Navigation, { className: "size-4" }) }), layers && onLayersChange && (_jsx("button", { onClick: () => setLayersMenu((v) => !v), title: "Couches d'analyse", "aria-expanded": layersMenu, className: `flex size-8 items-center justify-center rounded ${layers.zones || layers.hotspots ? 'bg-brand text-white' : 'text-muted-foreground hover:bg-muted hover:text-foreground'}`, children: _jsx(Layers, { className: "size-4" }) }))] }), layers && onLayersChange && layersMenu && (_jsxs("div", { className: "absolute left-14 top-3 z-[1000] w-64 rounded-lg border border-border bg-card p-3 text-xs shadow-lg", children: [_jsx("p", { className: "mb-2 font-semibold", children: "Couches d'analyse" }), _jsxs("label", { className: `flex items-start gap-2 py-1 ${zones?.length ? '' : 'opacity-60'}`, children: [_jsx("input", { type: "checkbox", checked: layers.zones, disabled: !zones?.length, onChange: (e) => onLayersChange({ ...layers, zones: e.target.checked }), className: "mt-0.5" }), _jsxs("span", { children: ["Quartiers / districts", !zones?.length && _jsx("span", { className: "block text-muted-foreground", children: "Aucune couche import\u00E9e pour cette ville" })] })] }), _jsxs("label", { className: "flex items-start gap-2 py-1", children: [_jsx("input", { type: "checkbox", checked: layers.hotspots, onChange: (e) => onLayersChange({ ...layers, hotspots: e.target.checked }), className: "mt-0.5" }), _jsxs("span", { children: ["Points chauds", _jsx("span", { className: "block text-muted-foreground", children: "Concentrations statistiquement significatives (Getis-Ord Gi*), selon les filtres" })] })] })] })), layers?.hotspots && hotspots && (_jsx("div", { className: "absolute bottom-12 right-3 z-[900] rounded-lg border border-border bg-card/95 p-2.5 text-[11px] shadow-md", children: hotspots.insufficient ? (_jsxs("p", { className: "max-w-[12rem] text-muted-foreground", children: ["Pas assez de signalements (", hotspots.total, ") pour une analyse significative : ", hotspots.minReports, " au minimum."] })) : (_jsxs(_Fragment, { children: [_jsxs("p", { className: "mb-1 font-semibold", children: ["Points chauds \u00B7 hexagones de ", hotspots.cellAreaKm2, " km\u00B2"] }), ['hot99', 'hot95', 'hot90', 'cold95'].map((k) => (_jsxs("p", { className: "flex items-center gap-1.5", children: [_jsx("span", { className: "inline-block size-3 rounded-sm", style: { background: HOTSPOT_STYLES[k].color, opacity: HOTSPOT_STYLES[k].opacity + 0.3 } }), HOTSPOT_STYLES[k].label] }, k)))] })) })), routeMode && (_jsxs("div", { className: "absolute bottom-4 left-4 z-[1000] w-80 rounded-lg border border-border bg-card shadow-lg", children: [_jsxs("div", { className: "flex items-center justify-between border-b border-border px-3 py-2", children: [_jsxs("p", { className: "text-sm font-semibold", children: ["Tourn\u00E9e (", routeStops.length, " arr\u00EAt", routeStops.length > 1 ? 's' : '', ")"] }), _jsx("button", { onClick: cancelRoute, className: "text-muted-foreground hover:text-foreground", "aria-label": "Fermer la tourn\u00E9e", children: _jsx(X, { className: "size-4" }) })] }), routeStops.length === 0 ? (_jsx("p", { className: "p-3 text-center text-xs italic text-muted-foreground", children: "Cliquez sur les signalements pour composer votre tourn\u00E9e" })) : (_jsxs(_Fragment, { children: [_jsx("ol", { className: "max-h-40 overflow-y-auto p-2 text-xs", children: routeStops.map((s, i) => (_jsxs("li", { className: "flex items-center gap-2 py-1", children: [_jsx("span", { className: "flex size-5 shrink-0 items-center justify-center rounded-full bg-brand text-[10px] font-bold text-white", children: i + 1 }), _jsx("span", { className: "flex-1 truncate", children: s.title }), _jsx("button", { onClick: () => {
                                                setRouteStops((prev) => prev.filter((x) => x.id !== s.id));
                                                if (routeSummary)
                                                    clearDrawnRoute();
                                            }, className: "text-muted-foreground hover:text-destructive", "aria-label": "Retirer l'arr\u00EAt", children: _jsx(X, { className: "size-3" }) })] }, s.id))) }), routeSummary && (_jsxs("div", { className: "mx-2 mb-2 flex items-center justify-between rounded-md bg-brand/10 px-3 py-1.5 text-xs", children: [_jsxs("span", { className: "flex items-center gap-1 font-semibold text-brand", children: [_jsx(RouteIcon, { className: "size-3" }), routeSummary.distanceKm, " km"] }), _jsxs("span", { className: "flex items-center gap-1 text-muted-foreground", children: [_jsx(Clock, { className: "size-3" }), "~", routeSummary.durationMin, " min"] })] })), _jsx("div", { className: "space-y-1.5 border-t border-border p-2", children: !routeSummary ? (_jsxs(_Fragment, { children: [_jsxs("label", { className: "flex items-center gap-2 px-1 text-xs", children: [_jsx("input", { type: "checkbox", checked: optimize, onChange: (e) => setOptimize(e.target.checked) }), _jsx(Shuffle, { className: "size-3 text-muted-foreground" }), "Optimiser l'ordre des arr\u00EAts (le 1er reste le d\u00E9part)"] }), _jsxs(Button, { variant: "brand", size: "sm", className: "w-full gap-1.5", disabled: routeStops.length < 2 || routeTracing, onClick: traceRoute, children: [routeTracing ? _jsx(Loader2, { className: "animate-spin" }) : _jsx(RouteIcon, { className: "size-3.5" }), routeTracing ? 'Calcul…' : 'Tracer l\'itinéraire'] })] })) : (_jsxs(_Fragment, { children: [_jsxs(Button, { variant: "outline", size: "sm", className: "w-full gap-1.5", onClick: clearDrawnRoute, children: [_jsx(X, { className: "size-3.5" }), "Effacer le trac\u00E9"] }), _jsxs(Button, { variant: "brand", size: "sm", className: "w-full gap-1.5", onClick: openGoogleMapsRoute, children: [_jsx(MapPin, { className: "size-3.5" }), "Ouvrir dans Google Maps"] })] })) })] }))] }))] }));
}
//# sourceMappingURL=ReportMap.js.map